import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { StatusPill } from '@/components/status-pill'
import { Switch } from '@/components/ui/switch'
import { formatApiErrorMessage } from '@/lib/api-error'
import { useQuoteBuilderApi } from '../api/client'
import { SurfaceCard } from '../ui/page'
import { followUpQueryKey, followUpQueryOptions, updateFollowUp, type QuoteFollowUp } from './api'

const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })

/** Sales' follow-up reminders for the quote, and marking it lost (USA-1286). */
export function FollowUpCard({ quoteId }: { quoteId: string }) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const followUp = useQuery(followUpQueryOptions(api, quoteId))
  const update = useMutation({
    mutationFn: (change: { reminders_paused?: boolean; lost?: boolean }) => updateFollowUp(api, quoteId, change),
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: followUpQueryKey(quoteId) })
      const previous = queryClient.getQueryData<QuoteFollowUp>(followUpQueryKey(quoteId))
      if (previous) queryClient.setQueryData(followUpQueryKey(quoteId), { ...previous, ...change })
      return { previous }
    },
    onError: (error, _change, context) => {
      if (context?.previous) queryClient.setQueryData(followUpQueryKey(quoteId), context.previous)
      toast.error(formatApiErrorMessage('Could not update follow-up', error))
    },
    onSuccess: (next) => queryClient.setQueryData(followUpQueryKey(quoteId), next),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['quote-activity', quoteId] }),
  })
  const state = followUp.data
  if (!state) return null
  const changingLost = update.isPending && update.variables?.lost !== undefined

  return (
    <SurfaceCard
      actions={
        <LoadingButton
          loading={changingLost}
          loadingText={state.lost ? 'Marking lost...' : 'Reopening...'}
          onClick={() => update.mutate({ lost: !state.lost })}
          size="sm"
          variant="outline"
        >
          {state.lost ? <RotateCcw aria-hidden="true" /> : <XCircle aria-hidden="true" />}
          {state.lost ? 'Reopen' : 'Mark lost'}
        </LoadingButton>
      }
      className="mb-4"
      title="Follow-up"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
        {state.lost ? (
          <StatusPill tone="danger">Lost</StatusPill>
        ) : (
          <>
            <label className="flex items-center gap-3 text-sm font-medium" htmlFor="quote-follow-up-reminders">
              <Switch
                checked={!state.reminders_paused}
                disabled={update.isPending && update.variables?.reminders_paused !== undefined}
                id="quote-follow-up-reminders"
                onCheckedChange={(on) => update.mutate({ reminders_paused: !on })}
              />
              Reminders every {state.reminder_business_days} business days
            </label>
            {state.next_reminder_on && !state.reminders_paused ? (
              <span className="text-sm text-muted-foreground">Next {day.format(new Date(state.next_reminder_on))}</span>
            ) : null}
          </>
        )}
      </div>
    </SurfaceCard>
  )
}
